import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { KaboodleToaster } from "components/common";
import { MaskedInput, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import {
  useArchiveClientPricingFieldMutation,
  useGetClientByIdQuery,
  usePatchClientByIdMutation,
  useRestoreClientPricingFieldMutation,
} from "services/client-api";
import { useDispatch, useSelector } from "utils/redux";

import ClientPageContainer from "./ClientPageContainer";
import FeeScheduleCard from "./FeeScheduleCard";
import ClientFilesDrawer from "./ClientFilesDrawer/ClientFilesDrawer";
import ClientDocumentsDrawer from "./ClientDocumentsDrawer/ClientDocumentsDrawer";
import ClientFeedsDrawer from "./ClientFeedsDrawer/ClientFeedsDrawer";
import ClientExportFeedsDrawer from "./ClientFeedsDrawer/ClientExportFeedsDrawer";

type InvoiceFormData = {
  billing_email: string;
  invoice_contact_name: string;
  invoicing_street: string;
  invoicing_street2: string;
  invoicing_city: string;
  invoicing_state: string;
  invoicing_zip_code: string;
  invoicing_country: string;
  custom_bill_rate_adjustments: number | null;
  // Medicare Set-Aside Services
  msa_preparation_standard: string;
  msa_preparation_complex: string;
  msa_111: string;
  msa_update: string;
  msa_re_preparation: string;
  pre_msa_snapshot: string;
  non_medicare_cost_report: string;
  non_threshold_msa: string;
  case_management_services: string;
  rated_age: string;
  // Medical Cost Projection Services
  medical_cost_projection: string;
  future_medical_allocation: string;
  future_medical_cost_projection: string;
  // Life Care Planning Services
  life_care_plan: string;
  life_care_planning_services: string;
  // CMS Review & Submission Services
  cms_submission: string;
  cms_re_review: string;
  cms_amended_review: string;
  cms_amended_review_evaluation: string;
  amended_review_submission: string;
  ten_ten_review: string;
  // Pharmacy / Clinical Cost Containment
  propharma_report: string;
  pharmacy_cost_containment_review: string;
  medication_allocation_review: string;
  // Rush Fees
  msa_three_day_rush: string;
  mcp_three_day_rush: string;
  lcp_three_day_rush: string;
  msa_twenty_four_hour_rush: string;
  mcp_twenty_four_hour_rush: string;
  lcp_twenty_four_hour_rush: string;
  // Conditional Payment Services
  conditional_payments: string;
  ff_lien_bundled: string;
  medicare_query_investigation: string;
  traditional_medicare_investigation: string;
  medicare_advantage_investigation: string;
  conditional_payment_itemization: string;
  conditional_payment_dispute: string;
  // Sliding Fee Scale
  sliding_fee_under_2500: string;
  sliding_fee_2501_to_14999: string;
  sliding_fee_15000_to_29999: string;
  sliding_fee_30000_to_49999: string;
  sliding_fee_50000_to_74999: string;
  sliding_fee_75000_to_99999: string;
  sliding_fee_100000_to_149999: string;
  sliding_fee_over_150000: string;
  // Hourly Rates (shared across Conditional Payment and Section 111)
  hourly_principal: string;
  hourly_vp: string;
  hourly_manager: string;
  hourly_analyst: string;
  recovery_agent_processing: string;
  // Section 111 Reporting Services
  software_license: string;
  annual_audit: string;
  report_claim_to_cms: string;
  section_111_general_ff: string;
  // Consulting
  release_settlement_language_review: string;
};

const InvoiceForm = () => {
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId?: string }>();

  const { data: client, isLoading } = useGetClientByIdQuery(
    { id: clientId },
    { skip: !clientId },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const [patchClientById] = usePatchClientByIdMutation();
  const [archivePricingField] = useArchiveClientPricingFieldMutation();
  const [restorePricingField] = useRestoreClientPricingFieldMutation();
  // Fee type with an archive/restore request in flight. Tracked per field
  // (not via the mutations' shared isLoading) so only that row is disabled.
  const [pendingPricingField, setPendingPricingField] = useState<string | null>(
    null,
  );

  const constants = useSelector((state) => state.shared.constants);

  // Fee types archived for this client. Archived fee types are hidden from
  // the schedule below and cannot be picked for new invoice line items.
  const archivedPricingFields = useMemo(
    () => client?.archived_pricing_fields ?? [],
    [client],
  );

  // Which fields exist and which section each belongs to comes from the
  // constants API (INVOICE_PRICING_FIELD_GROUPS) instead of being listed
  // here, so this form can't drift out of sync with the backend schedule.
  const pricingFieldGroups = constants?.INVOICE_PRICING_FIELD_GROUPS ?? [];
  const pricingFieldNames = useMemo(
    () => pricingFieldGroups.flatMap((group) => group.fields),
    [pricingFieldGroups],
  );
  const pricingFieldLabels = useMemo(
    () =>
      Object.fromEntries(
        (constants?.DEFAULT_INVOICE_PRICING_FIELDS ?? []).map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [constants],
  );

  const {
    control,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<InvoiceFormData>({
    defaultValues: {
      billing_email: "",
      invoice_contact_name: "",
      invoicing_street: "",
      invoicing_street2: "",
      invoicing_city: "",
      invoicing_state: "",
      invoicing_zip_code: "",
      invoicing_country: "",
      custom_bill_rate_adjustments: null,
      // Pricing field names aren't known statically since they come from
      // the constants API, so the merged object can't check against
      // InvoiceFormData until after the spread. Cast through unknown
      // rather than any, since we do know the shape once it's built.
      ...Object.fromEntries(pricingFieldNames.map((name) => [name, ""])),
    } as unknown as InvoiceFormData,
  });

  useEffect(() => {
    if (client && pricingFieldNames.length > 0) {
      // The fee schedule field names come from the constants API, so they
      // are looked up dynamically. ClientType only declares a stale subset
      // of the price columns, so index through a string-keyed view of the
      // client for those; everything else is read off the typed client.
      const pricingValues = client as unknown as Record<
        string,
        string | null | undefined
      >;

      if (!client.quickbooks_workspace) {
        KaboodleToaster.info(
          "Please complete QuickBooks integration setup to enable billing email.",
        );
      }

      // `|| ""` rather than `?? ""` below is safe here even for a
      // legitimately $0 price: DRF serializes DecimalFields as strings
      // (e.g. "0.00"), and a non-empty string is truthy, so it's never
      // coerced away. Only a real null/undefined falls through to "".
      const pricingData = Object.fromEntries(
        pricingFieldNames.map((name) => [name, pricingValues[name] || ""]),
      );

      // Same reasoning as defaultValues above: names come from the API.
      const invoiceData = {
        billing_email: client.billing_email || "",
        invoice_contact_name: client.invoice_contact_name || "",
        invoicing_street: client.invoicing_street || "",
        invoicing_street2: client.invoicing_street2 || "",
        invoicing_city: client.invoicing_city || "",
        invoicing_state: client.invoicing_state || "",
        invoicing_zip_code: client.invoicing_zip_code || "",
        invoicing_country: client.invoicing_country || "",
        custom_bill_rate_adjustments: client.custom_bill_rate_adjustments,
        ...pricingData,
      } as unknown as InvoiceFormData;

      reset(invoiceData, { keepDirtyValues: true });
    }
  }, [client, reset, pricingFieldNames]);

  const handleSave = useCallback(
    async (field: keyof InvoiceFormData, value: string | number | boolean) => {
      if (clientId) {
        try {
          await patchClientById({
            id: clientId,
            data: { [field]: value },
          }).unwrap();
          clearErrors(field);
        } catch (error: unknown) {
          const apiError = error as {
            status: number;
            data: Record<string, string[]>;
          };
          KaboodleToaster.error(
            apiError.data[field]?.[0] || "An error occurred",
          );
          setError(field, {
            type: "manual",
            message: apiError.data[field]?.[0] || "An error occurred",
          });
        }
      }
    },
    [patchClientById, clientId, setError, clearErrors],
  );

  const handleArchivePricingField = useCallback(
    async (name: string) => {
      if (!clientId) return;
      const label = pricingFieldLabels[name] ?? name;
      setPendingPricingField(name);
      try {
        await archivePricingField({
          id: clientId,
          pricingField: name,
        }).unwrap();
        KaboodleToaster.success(
          `${label} archived. It can no longer be added to this client's invoices.`,
        );
      } catch {
        KaboodleToaster.error(`Failed to archive ${label}`);
      } finally {
        setPendingPricingField(null);
      }
    },
    [archivePricingField, clientId, pricingFieldLabels],
  );

  const handleRestorePricingField = useCallback(
    async (name: string) => {
      if (!clientId) return;
      const label = pricingFieldLabels[name] ?? name;
      setPendingPricingField(name);
      try {
        await restorePricingField({
          id: clientId,
          pricingField: name,
        }).unwrap();
        KaboodleToaster.success(`${label} restored.`);
      } catch {
        KaboodleToaster.error(`Failed to restore ${label}`);
      } finally {
        setPendingPricingField(null);
      }
    },
    [restorePricingField, clientId, pricingFieldLabels],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };

  // Every pricing field goes through this helper, so the dollar mask is
  // consistent validation for all of them, same as other currency
  // fields elsewhere in the app.
  const dollarInput = (name: keyof InvoiceFormData, label: string) => (
    <ControlledInput
      key={name}
      control={control}
      name={name}
      label={label}
      errors={errors}
      onSave={(value) => handleSave(name, value)}
      componentType={MaskedInput}
      mask="dollar"
    />
  );

  return (
    <>
      <ClientPageContainer>
        <form className="overview" onKeyDown={handleKeyDown} autoComplete="off">
          <Card heading="General Invoicing Settings">
            <ControlledInput
              control={control}
              name="billing_email"
              label="Billing Email"
              errors={errors}
              onSave={(value) => handleSave("billing_email", value)}
              componentType={TextInput}
              readOnly={!client?.quickbooks_workspace}
            />

            <ControlledInput
              control={control}
              name="invoice_contact_name"
              label="Invoice Contact Name"
              errors={errors}
              onSave={(value) => handleSave("invoice_contact_name", value)}
              componentType={TextInput}
            />

            <ControlledInput
              control={control}
              name="invoicing_street"
              label="Invoicing Street Address"
              errors={errors}
              onSave={(value) => handleSave("invoicing_street", value)}
              componentType={TextInput}
            />

            <ControlledInput
              control={control}
              name="invoicing_street2"
              label="Invoicing Street Address 2"
              errors={errors}
              onSave={(value) => handleSave("invoicing_street2", value)}
              componentType={TextInput}
            />

            <ControlledInput
              control={control}
              name="invoicing_city"
              label="Invoicing City"
              errors={errors}
              onSave={(value) => handleSave("invoicing_city", value)}
              componentType={TextInput}
            />

            <ControlledSelect
              control={control}
              name="invoicing_state"
              errors={errors}
              label="Invoicing State"
              onSave={(o) => handleSave("invoicing_state", o?.value || "")}
              options={constants?.STATES || []}
            />

            <ControlledInput
              control={control}
              name="invoicing_zip_code"
              label="Invoicing Zip Code"
              errors={errors}
              onSave={(value) => handleSave("invoicing_zip_code", value)}
              componentType={MaskedInput}
              mask="zipcode"
            />

            <ControlledInput
              control={control}
              name="invoicing_country"
              label="Invoicing Country"
              errors={errors}
              onSave={(value) => handleSave("invoicing_country", value)}
              componentType={TextInput}
              maxLength={2}
            />

            <ControlledInput
              control={control}
              name="custom_bill_rate_adjustments"
              label="Custom Bill Rate Adjustments"
              errors={errors}
              onSave={(value) =>
                handleSave("custom_bill_rate_adjustments", value)
              }
              componentType={MaskedInput}
              mask="dollar"
            />
          </Card>

          {pricingFieldGroups.map((group) => (
            <FeeScheduleCard
              key={group.name}
              heading={group.name}
              fields={group.fields}
              labels={pricingFieldLabels}
              archivedFields={archivedPricingFields}
              // Field names come from the constants API, so TypeScript
              // can't statically confirm they're keys of InvoiceFormData.
              renderInput={(name, label) =>
                dollarInput(name as keyof InvoiceFormData, label)
              }
              onArchive={handleArchivePricingField}
              onRestore={handleRestorePricingField}
              pendingField={pendingPricingField}
            />
          ))}
        </form>
      </ClientPageContainer>
      <ClientFilesDrawer />
      <ClientDocumentsDrawer />
      <ClientFeedsDrawer />
      <ClientExportFeedsDrawer />
    </>
  );
};

export default InvoiceForm;
